import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import React from 'react';

/** react-markdown's `components.img` override type. */
export type MarkdownImageComponent = (props: React.ComponentProps<'img'>) => React.ReactElement;

/**
 * Build the `img` override for react-markdown's `components` map: images are never
 * rendered as <img> (no `src` ever reaches the DOM), only the alt text (if any) is
 * shown as inert placeholder text. Shared across every markdown surface (chat
 * messages, FAQ create/edit preview, FAQ detail view, FAQ list cards) so a
 * user-authored `![](https://...)` markdown image can never trigger an outbound
 * request just by being rendered.
 */
export function createMarkdownImageOverride(t: TFunction): MarkdownImageComponent {
  return ({ alt }: React.ComponentProps<'img'>) => {
    const label = alt
      ? t('chat.markdown.imageBlockedWithAlt', { alt, defaultValue: `Image blocked: ${alt}` })
      : t('chat.markdown.imageBlocked', { defaultValue: 'Image blocked' });
    return (
      <span
        role="img"
        aria-label={label}
        style={{
          display: 'inline-block',
          fontStyle: 'italic',
          color: '#666666',
          fontSize: '0.85em',
        }}
      >
        {label}
      </span>
    );
  };
}

/**
 * Hook form of {@link createMarkdownImageOverride} for components that don't already
 * hold a `t` reference in scope.
 */
export function useMarkdownImageOverride(): MarkdownImageComponent {
  const { t } = useTranslation();
  return createMarkdownImageOverride(t);
}
